const LIKES_STORAGE_KEY = "tulocalmx_property_likes_v1";
const VISITOR_STORAGE_KEY = "tulocalmx_visitor_id_v1";

const canUseStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const createVisitorId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `visitor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readLikes = () => {
  if (!canUseStorage()) return [];

  try {
    const stored = window.localStorage.getItem(LIKES_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeLikes = (likes) => {
  if (!canUseStorage()) return;
  window.localStorage.setItem(LIKES_STORAGE_KEY, JSON.stringify(likes));
};

export const getVisitorId = () => {
  if (!canUseStorage()) return "";

  const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY);
  if (existing) return existing;

  const visitorId = createVisitorId();
  window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId);
  return visitorId;
};

export const getLikedProperties = () => readLikes();

export const hasLikedProperty = (propertyId) =>
  readLikes().some((like) => String(like.id) === String(propertyId));

export const savePropertyLike = async (property) => {
  const likes = readLikes();
  const alreadyLiked = likes.some((like) => String(like.id) === String(property.id));

  if (alreadyLiked) {
    return { liked: true, created: false, likes };
  }

  const nextLikes = [
    ...likes,
    {
      id: property.id,
      title: property.title,
      location: property.location || property.address || "",
      likedAt: new Date().toISOString(),
    },
  ];

  writeLikes(nextLikes);
  window.dispatchEvent(new Event("tulocalmx:property-likes-changed"));

  try {
    const response = await fetch("/api/property-likes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        propertyId: property.id,
        title: property.title,
        location: property.location || property.address || "",
        visitorId: getVisitorId(),
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return { liked: true, created: data.created !== false, likes: nextLikes, count: data.count };
    }
  } catch (error) {
    // The local favorite still works if the remote counter is not configured yet.
  }

  return { liked: true, created: true, likes: nextLikes };
};

export const getPropertyLikeCount = async (propertyId) => {
  try {
    const response = await fetch(`/api/property-likes?propertyId=${encodeURIComponent(propertyId)}`);
    if (!response.ok) return null;
    const data = await response.json();
    return Number.isFinite(data.count) ? data.count : null;
  } catch (error) {
    return null;
  }
};

export const getLikedPropertiesSummary = () => {
  const likes = readLikes();
  if (!likes.length) return "";

  return likes.map((like) => `- ${like.title} (ID ${like.id})`).join("\n");
};
